import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { ConnectorStrip } from '@/components/layout/connector-strip';
import {
  NewStrategyButton,
  RefreshButton,
} from '@/components/layout/top-bar-global-actions';
import {
  TopBarActions,
  TopBarConnectors,
  TopBarDateRange,
} from '@/components/layout/top-bar-slots';
import { TOUR_ANCHOR } from '@/components/tour/anchors';
import { type DateRangeParams } from '@/lib/date-range';
import { DashboardStrategiesSection } from '@/components/dashboard/dashboard-strategies-section';
import { KpiSection } from '@/components/dashboard/overview/kpi-section';
import { PeriodSelector } from '@/components/dashboard/performance/period-selector';
import { AttentionRow } from '@/components/dashboard/recommendations/attention-row';
import { WorkspaceRow } from '@/components/dashboard/workspace/workspace-row';
import { useBusinessModel } from '@/hooks/data/use-auth';
import { useDashboardStrategies } from '@/hooks/data/use-dashboard';
import { useOrgKpis } from '@/hooks/data/use-org-kpis';

const searchSchema = z.object({
  date_range: z
    .enum(['LAST_7D', 'LAST_14D', 'LAST_30D', 'LAST_90D', 'CUSTOM'])
    .default('LAST_30D'),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/dashboard')({
  validateSearch: searchSchema,
  staticData: {
    title: 'route.dashboard.title',
    messageContext: { context_type: 'workspace' },
  },
  component: DashboardPage,
});

const PAGE_LIMIT = 50;

const TABLE_PAGE_SIZE = 10;

function DashboardPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const businessModel = useBusinessModel();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [unified, setUnified] = useState(false);

  const range: DateRangeParams = useMemo(
    () => ({
      date_range: search.date_range,
      start_date: search.start_date,
      end_date: search.end_date,
    }),
    [search.date_range, search.start_date, search.end_date],
  );

  const current = useDashboardStrategies({
    date_range: range.date_range,
    start_date: range.start_date,
    end_date: range.end_date,
    page: 1,
    limit: PAGE_LIMIT,
  });

  const orgKpis = useOrgKpis(range);
  const unifyOffered = orgKpis.currencyMixed && orgKpis.hasUnifiedTotal;
  // Kept apart from the switch state, so a range that hides the switch cannot leave it on.
  const showUnified = unifyOffered && unified;

  const handlePeriodChange = (next: DateRangeParams): void => {
    setPage(1);
    void navigate({
      to: Route.fullPath,
      search: {
        date_range: next.date_range,
        start_date: next.start_date,
        end_date: next.end_date,
      },
      replace: true,
    });
  };

  const handleSelectedIdsChange = (next: number[]): void => {
    setSelectedIds(next);
  };

  return (
    <>
      <TopBarDateRange>
        <div
          data-tour-anchor={TOUR_ANCHOR.dateRange}
          className="flex items-center gap-2"
        >
          <PeriodSelector value={range} onChange={handlePeriodChange} />
          <RefreshButton />
        </div>
      </TopBarDateRange>
      <TopBarActions>
        <NewStrategyButton
          location="dashboard"
          tourAnchor={TOUR_ANCHOR.newStrategy}
        />
      </TopBarActions>
      <TopBarConnectors>
        <ConnectorStrip />
      </TopBarConnectors>
      <main className="mx-auto flex w-full max-w-310 flex-1 flex-col gap-7 px-4 pt-5 pb-12 sm:px-7 sm:pt-6 sm:pb-30">
        <KpiSection
          businessModel={businessModel}
          kpis={orgKpis}
          range={range}
          unifyOffered={unifyOffered}
          unified={showUnified}
          onUnifiedChange={setUnified}
        />
        <AttentionRow strategies={current.data?.items} />
        <DashboardStrategiesSection
          businessModel={businessModel}
          current={current}
          range={range}
          selectedIds={selectedIds}
          onSelectedIdsChange={handleSelectedIdsChange}
          page={page}
          onPageChange={setPage}
          pageSize={TABLE_PAGE_SIZE}
          unified={showUnified}
          unifyOffered={unifyOffered}
        />
        <WorkspaceRow range={range} />
      </main>
    </>
  );
}
